/**
 * Dziki Pan quiz ("Testerzy Dziki Pan").
 * State machine: start → q[0..n-1] → contact → end. Content from <script data-dp-quiz-config>.
 * Every step is logged (view/answer/back/abandon + timing) so the funnel can be read per question.
 * Modes: full (Worker endpoint) / lite (Shopify customer form + shared code).
 */

const SS_KEY = 'dp-quiz-state-v1';
const LS_KEY = 'dp-quiz-seen';
const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

class DpQuiz extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    const { signal } = this.#abort;
    this.cfg = JSON.parse(this.querySelector('[data-dp-quiz-config]')?.textContent || '{"questions":[]}');
    this.products = JSON.parse(this.querySelector('[data-dp-quiz-products]')?.textContent || '[]').filter((p) => p.key);
    this.endpoint = (this.dataset.endpoint || '').replace(/\/$/, '');
    this.sheet = /script\.google\.com/.test(this.endpoint); // Google Apps Script web app: collects data, customer still via Shopify form
    this.lite = !this.endpoint || this.sheet;
    this.$ = (sel) => this.querySelector(sel);
    this.screens = Object.fromEntries([...this.querySelectorAll('[data-dp-screen]')].map((el) => [el.dataset.dpScreen, el]));
    this.progress = this.$('[data-dp-progress]');
    this.bar = this.$('[data-dp-bar]');
    this.next = this.$('[data-dp-next]');
    this.queue = [];

    const v = new URL(location.href).searchParams.get('v');
    if (v === 'b' || v === 'a') this.dataset.variant = v;
    const h1 = this.$('[data-dp-h1]');
    if (h1 && h1.dataset[`h1${this.dataset.variant === 'b' ? 'B' : 'A'}`]) h1.textContent = h1.dataset[`h1${this.dataset.variant === 'b' ? 'B' : 'A'}`];
    this.state = this.restore() || this.fresh();
    if (v) this.state.variant = v;
    this.saveSeen();
    this.installPixel();

    for (const b of this.querySelectorAll('[data-dp-start]')) b.addEventListener('click', () => this.start(b.dataset.for === 'him'), { signal });
    this.next.addEventListener('click', () => this.advance(), { signal });
    this.$('[data-dp-back]')?.addEventListener('click', () => this.back(), { signal });
    this.$('[data-dp-back-contact]')?.addEventListener('click', () => this.back(), { signal });
    this.$('[data-dp-contact]')?.addEventListener('submit', (e) => this.submit(e), { signal });
    this.$('[data-dp-reserve]')?.addEventListener('click', () => this.reserve(), { signal });
    this.$('[data-dp-preorder]')?.addEventListener('click', (e) => this.preorder(e), { signal });
    this.$('[data-dp-bundles]')?.addEventListener('click', () => this.track('bundles_click', { product: this.state.product }), { signal });
    this.$('[data-dp-code]')?.addEventListener('click', () => this.track('code_click', {}), { signal });
    this.$('[data-dp-copy]')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget; const code = this.state.code || '';
      let ok = false;
      try { await navigator.clipboard.writeText(code); ok = true; } catch { try { const r = document.createRange(); r.selectNodeContents(this.$('[data-dp-code]')); const sel = getSelection(); sel.removeAllRanges(); sel.addRange(r); ok = document.execCommand('copy'); } catch {} }
      btn.textContent = ok ? btn.dataset.done : btn.dataset.label; btn.classList.toggle('is-done', ok);
      setTimeout(() => { btn.textContent = btn.dataset.label; btn.classList.remove('is-done'); }, 2200);
      this.track('code_copy', { ok });
    }, { signal });
    document.addEventListener('keydown', (e) => { if (e.key === 'Enter' && this.state.screen === 'question' && !this.next.disabled && !(e.target instanceof HTMLTextAreaElement)) { e.preventDefault(); this.advance(); } }, { signal });
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') this.flush(true); }, { signal });
    window.addEventListener('pagehide', () => this.flush(true), { signal });

    const url0 = new URL(location.href);
    const done = url0.searchParams.get('done') === '1' || url0.searchParams.has('customer_posted') || url0.hash === '#contact_form';
    if (this.state.pending_end) { this.finishEnd(); return; }
    if (done && this.state.screen !== 'end') { this.state.screen = 'start'; }
    this.render();
    if (this.state.screen === 'start') { this.track('view_start', { variant: this.dataset.variant }); this.pixel('ViewContent', { content_name: 'quiz' }); }
  }

  disconnectedCallback() { this.#abort.abort(); this.flush(true); }

  /* ---------- state ---------- */
  fresh() {
    const url = new URL(location.href);
    const utm = {};
    for (const [k, v] of url.searchParams) if (/^(utm_|fbclid|gclid|ttclid)/.test(k)) utm[k] = v;
    return {
      session: uid(), started_at: null, screen: 'start', q: 0, answers: {}, meta: {}, for: false,
      variant: this.dataset.variant || 'a', utm, referrer: document.referrer || '', landing: location.pathname + location.search,
      device: { ua: navigator.userAgent, w: innerWidth, h: innerHeight, dpr: devicePixelRatio || 1, touch: 'ontouchstart' in window, lang: navigator.language, tz: Intl.DateTimeFormat().resolvedOptions().timeZone, mobile: innerWidth < 768 },
      returning: !!localStorage.getItem(LS_KEY), timings: {}, changes: {}, order: {}, backs: 0, maxq: 0, product: null, end_action: null, code: null,
    };
  }

  restore() { try { const s = JSON.parse(sessionStorage.getItem(SS_KEY) || 'null'); return s && s.session ? s : null; } catch { return null; } }
  save() { try { sessionStorage.setItem(SS_KEY, JSON.stringify(this.state)); } catch {} }
  saveSeen() { try { localStorage.setItem(LS_KEY, String(Date.now())); } catch {} }

  /* ---------- tracking ---------- */
  track(event, payload = {}) {
    const rec = { session: this.state.session, ts: Date.now(), event, step: this.state.screen === 'question' ? this.cfg.questions[this.state.q]?.id : this.state.screen, variant: this.state.variant, for: this.state.for, ...payload };
    this.queue.push(rec);
    try { window.Shopify?.analytics?.publish?.('dp_quiz', rec); } catch {}
    if (this.queue.length >= 5) this.flush();
    else { clearTimeout(this.flushT); this.flushT = setTimeout(() => this.flush(), 2500); }
  }

  /** fire-and-forget POST (sendBeacon → keepalive fetch); Apps Script needs text/plain to avoid preflight */
  beacon(url, obj) {
    const body = JSON.stringify(obj);
    try { if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))) return; } catch {}
    fetch(url, { method: 'POST', mode: 'no-cors', headers: { 'Content-Type': 'text/plain' }, body, keepalive: true }).catch(() => {});
  }

  flush(beacon = false) {
    if (!this.endpoint || !this.queue.length) { this.queue = []; return; }
    const payload = { type: 'event', session: this.state.session, utm: this.state.utm, variant: this.state.variant, entry: this.state.entry || 'me', events: this.queue.splice(0) };
    if (this.sheet) { this.beacon(this.endpoint, payload); return; }
    const body = JSON.stringify(payload);
    const url = `${this.endpoint}/quiz/event`;
    if (beacon && navigator.sendBeacon) { navigator.sendBeacon(url, new Blob([body], { type: 'application/json' })); return; }
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  }

  installPixel() {
    const id = this.dataset.pixel;
    if (!id || window.fbq) return;
    /* Meta Pixel base code */
    // eslint-disable-next-line
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('init', id);
    window.fbq('track', 'PageView');
  }

  pixel(name, params = {}, custom = false) {
    const eventID = uid();
    try { if (window.fbq) window.fbq(custom ? 'trackCustom' : 'track', name, params, { eventID }); } catch {}
    this.track('pixel', { name, params, event_id: eventID });
    return eventID;
  }

  /* ---------- navigation ---------- */
  start(forHim = false) {
    this.state.for = !!forHim; this.state.entry = forHim ? 'him' : 'me';
    this.state.started_at = this.state.started_at || Date.now();
    this.state.screen = 'question'; this.state.q = 0;
    this.enterQuestion();
    this.pixel('QuizStart', { variant: this.state.variant, entry: this.state.entry }, true);
  }

  enterQuestion(dir = 1) {
    this.qDir = dir;
    this.qEnter = Date.now();
    this.state.maxq = Math.max(this.state.maxq, this.state.q);
    this.save(); this.render();
    this.track('view_question', { index: this.state.q });
    scrollTo({ top: 0, behavior: 'smooth' });
  }

  leaveQuestion() {
    const id = this.cfg.questions[this.state.q]?.id;
    if (id) this.state.timings[id] = (this.state.timings[id] || 0) + (Date.now() - (this.qEnter || Date.now()));
  }

  advance() {
    const q = this.cfg.questions[this.state.q];
    if (!q) return;
    if (q.type === 'text') { const ta = this.$('[data-dp-ta]'); this.state.answers[q.id] = (ta?.value || '').trim(); }
    this.leaveQuestion();
    this.track('answer', { index: this.state.q, value: this.state.answers[q.id] ?? null, ms: this.state.timings[q.id], changes: this.state.changes[q.id] || 0 });
    if (this.state.q + 1 < this.cfg.questions.length) { this.state.q += 1; this.enterQuestion(1); return; }
    this.state.screen = 'contact'; this.save(); this.render(); this.track('view_contact', {}); scrollTo({ top: 0, behavior: 'smooth' });
  }

  back() {
    if (this.state.screen === 'contact') { this.state.screen = 'question'; this.state.q = this.cfg.questions.length - 1; this.state.backs += 1; this.track('back', { to: this.state.q }); this.enterQuestion(-1); return; }
    if (this.state.screen !== 'question') return;
    this.leaveQuestion();
    this.state.backs += 1;
    if (this.state.q === 0) { this.state.screen = 'start'; this.track('back', { to: 'start' }); this.save(); this.render(); return; }
    this.state.q -= 1; this.track('back', { to: this.state.q }); this.enterQuestion(-1);
  }

  /* ---------- rendering ---------- */
  render() {
    const sc = this.state.screen;
    for (const [k, el] of Object.entries(this.screens)) el.hidden = k !== sc;
    const n = this.cfg.questions.length;
    const stepIdx = sc === 'question' ? this.state.q : sc === 'contact' ? n : sc === 'end' ? n + 1 : -1;
    this.progress.hidden = sc === 'start' || sc === 'end';
    this.progress.style.setProperty('--dp-quiz-progress', `${Math.max(0, stepIdx) / (n + 1) * 100}%`);
    this.bar.hidden = sc !== 'question';
    if (sc === 'contact') {
      const him = !!this.state.for;
      const h = this.$('[data-dp-contact-h]'); if (h) h.textContent = him ? h.dataset.him : h.dataset.me;
      const nl = this.$('[data-dp-name-label]'); if (nl) nl.firstChild.textContent = (him ? nl.dataset.him : nl.dataset.me) + ' ';
      const hw = this.$('[data-dp-hisname-wrap]'); if (hw) hw.hidden = !him;
    }
    if (sc === 'question') this.renderQuestion();
    if (sc === 'end') this.renderEnd();
  }

  text(q, key) { return (this.state.for && q[key + 'For']) || q[key] || ''; }

  renderQuestion() {
    const q = this.cfg.questions[this.state.q];
    const box = this.$('[data-dp-options]');
    const screen = this.screens.question;
    screen.classList.remove('is-entering');
    screen.style.setProperty('--dp-dir', `${(this.qDir || 1) * 24}px`);
    const titleEl = this.$('[data-dp-qtitle]'); const hint = this.$('[data-dp-qhint]');
    this.$('[data-dp-qnum]').textContent = `Pytanie ${this.state.q + 1} z ${this.cfg.questions.length}`;
    let title = this.text(q, 'title');
    if (q.id === 'cena') { const p = this.productByKey(this.state.answers.produkt); title = title.replace('{produkt}', p?.name || 'Produkt').replace('{cena}', p?.priceLabel || ''); }
    titleEl.textContent = title;
    const hintText = this.text(q, 'hint');
    hint.hidden = !hintText; hint.textContent = hintText;
    box.innerHTML = '';
    box.className = `dp-quiz__options${q.type === 'text' ? ' dp-quiz__options--1' : ''}`;
    box.style.setProperty('--dp-quiz-cols', String(q.cols || 2));

    if (q.type === 'text') {
      const ta = document.createElement('textarea');
      ta.className = 'dp-quiz__textarea'; ta.setAttribute('data-dp-ta', ''); ta.placeholder = q.placeholder || ''; ta.maxLength = 600; ta.value = this.state.answers[q.id] || '';
      ta.addEventListener('input', () => { this.next.disabled = false; });
      box.appendChild(ta);
      this.next.disabled = !q.optional && !ta.value;
      void screen.offsetWidth;
      screen.classList.add('is-entering');
      return;
    }

    let options = q.options || [];
    if (q.type === 'product') {
      options = this.products.map((p) => ({ v: p.key, t: p.name, sub: p.cardText || p.size, img: p.img, color: true }));
      if (q.shuffle) {
        if (!this.state.order[q.id]) this.state.order[q.id] = options.map((o) => o.v).sort(() => Math.random() - 0.5);
        const ord = this.state.order[q.id]; options = [...options].sort((a, b) => ord.indexOf(a.v) - ord.indexOf(b.v));
      }
    }
    const selected = new Set([].concat(this.state.answers[q.id] ?? []));
    for (const o of options) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `dp-quiz__opt${q.plain ? ' dp-quiz__opt--plain' : ''}${q.color || o.color ? ' dp-quiz__opt--color' : ''}`;
      btn.dataset.value = o.v;
      btn.setAttribute('aria-pressed', selected.has(o.v) ? 'true' : 'false');
      const label = (this.state.for && o.tFor) || o.t;
      const imgSrc = o.img && !/^https?:|^\/\//.test(o.img) && !o.img.startsWith('/') ? `${this.assetBase()}${o.img}${this.dataset.assetV ? '?v=' + this.dataset.assetV : ''}` : o.img;
      btn.innerHTML = `${o.img && !q.plain ? `<span class="dp-quiz__opt-img"><img src="${imgSrc}" alt="" loading="lazy" onerror="this.parentElement.classList.add('is-missing')"></span>` : ''}${o.price ? `<span class="dp-quiz__price-tag">${o.price}</span>` : ''}<span class="dp-quiz__opt-body"><span class="dp-quiz__opt-mark" aria-hidden="true"></span><span>${label}${o.sub ? `<span class="dp-quiz__opt-sub">${o.sub}</span>` : ''}</span></span>`;
      btn.addEventListener('click', () => this.choose(q, o, btn));
      box.appendChild(btn);
    }
    this.next.disabled = selected.size === 0;
    [...box.children].forEach((el, i) => el.style.setProperty('--dp-i', String(i)));
    void screen.offsetWidth;
    screen.classList.add('is-entering');
  }

  assetBase() {
    if (!this._assetBase) { const s = document.querySelector('script[src*="dp-quiz.js"]')?.getAttribute('src') || ''; this._assetBase = s.replace(/dp-quiz\.js.*$/, ''); }
    return this._assetBase;
  }

  choose(q, o, btn) {
    const box = btn.parentElement;
    const prev = this.state.answers[q.id];
    if (q.type === 'multi' || q.multi) {
      let arr = [].concat(prev ?? []);
      if (o.exclusive) arr = arr.includes(o.v) ? [] : [o.v];
      else { arr = arr.filter((v) => !(q.options || []).find((x) => x.v === v && x.exclusive)); arr = arr.includes(o.v) ? arr.filter((v) => v !== o.v) : [...arr, o.v]; if (q.max && arr.length > q.max) arr = arr.slice(-q.max); }
      this.state.answers[q.id] = arr;
      if (q.type === 'product') this.state.product = arr[0] || null;
      for (const b of box.querySelectorAll('.dp-quiz__opt')) b.setAttribute('aria-pressed', arr.includes(b.dataset.value) ? 'true' : 'false');
      this.next.disabled = arr.length === 0;
    } else {
      this.state.answers[q.id] = o.v;
      for (const b of box.querySelectorAll('.dp-quiz__opt')) b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      if (q.switch) this.state.for = !!o.for;
      if (q.type === 'product') this.state.product = o.v;
      this.next.disabled = false;
      clearTimeout(this.autoT); this.autoT = setTimeout(() => { if (this.state.screen === 'question' && this.cfg.questions[this.state.q] === q) this.advance(); }, 320); // single choice auto-advances
    }
    if (prev !== undefined) this.state.changes[q.id] = (this.state.changes[q.id] || 0) + 1;
    this.save();
  }

  /* ---------- contact / submit ---------- */
  async submit(e) {
    e.preventDefault();
    const form = e.currentTarget; const err = this.$('[data-dp-err]'); const btn = this.$('[data-dp-submit]');
    const email = String(this.$('[data-dp-email]')?.value || '').trim(); const name = String(this.$('[data-dp-name]')?.value || '').trim();
    const fd = new FormData(form);
    const problems = [];
    if (name.length < 2) problems.push('imię');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) problems.push('e-mail');
    if (!fd.get('marketing')) problems.push('zgoda na maile');
    if (!fd.get('terms')) problems.push('regulamin');
    if (problems.length) { err.hidden = false; err.textContent = `Sprawdź: ${problems.join(', ')}.`; this.track('contact_error', { fields: problems }); return; }
    err.hidden = true; btn.disabled = true; const orig = btn.textContent; btn.textContent = 'Chwila…';
    const hisName = String(this.$('[data-dp-hisname]')?.value || '').trim();
    this.state.meta.contact = { name, his_name: hisName || null, email, email_domain: email.split('@')[1] };
    this.state.answers.jego_imie = hisName || undefined;
    this.state.finished_at = Date.now();
    const payload = this.payload(name, email);
    this.track('submit', { mode: this.lite ? 'lite' : 'full', duration_ms: payload.duration_ms });

    if (this.lite) {
      /* Shopify customer form: native submit (passes bot challenge), Shopify redirects back to ?done=1 */
      this.$('[data-dp-tags]').value = this.tags().join(', ');
      const hn = this.$('[data-dp-hisname]'); if (hn) hn.removeAttribute('name');
      this.$('[data-dp-note]').value = JSON.stringify({ answers: payload.answers, product: payload.product, utm: payload.utm, variant: payload.variant, for: payload.for, duration_ms: payload.duration_ms }).slice(0, 5000);
      this.state.code = this.dataset.liteCode || '';
      if (this.sheet) this.beacon(this.endpoint, { type: 'submit', ...payload, code: this.state.code });
      this.flush(true);
      this.state.screen = 'end'; this.state.pending_end = true; this.save();
      form.submit();
      return;
    }
    let code = null; let ok = false;
    try {
      const r = await fetch(`${this.endpoint}/quiz`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      const j = await r.json().catch(() => ({})); ok = r.ok && !j.error; code = j.code || null;
    } catch { ok = false; }
    if (!ok) { btn.disabled = false; btn.textContent = orig; err.hidden = false; err.textContent = 'Nie udało się wysłać. Spróbuj jeszcze raz.'; this.track('submit_error', {}); return; }
    this.state.code = code || '';
    this.finishEnd();
  }

  finishEnd() {
    this.state.screen = 'end'; this.state.pending_end = false; this.save(); this.render();
    this.pixel('Lead', { content_name: 'quiz', product: this.state.product });
    this.pixel('QuizComplete', { product: this.state.product }, true);
    this.track('view_end', { product: this.state.product, mode: this.lite ? 'lite' : 'full' });
    scrollTo({ top: 0, behavior: 'smooth' });
  }

  payload(name, email) {
    const s = this.state;
    return { session: s.session, name, email, consent_marketing: true, consent_terms: true, answers: s.answers, for: s.for, entry: s.entry || 'me', product: s.product, variant: s.variant, utm: s.utm, referrer: s.referrer, landing: s.landing, device: s.device, returning: s.returning, timings: s.timings, changes: s.changes, order: s.order, backs: s.backs, started_at: s.started_at, finished_at: s.finished_at, duration_ms: s.finished_at - s.started_at, tags: this.tags() };
  }

  tags() {
    const a = this.state.answers; const t = ['quiz', `quiz-h1-${this.state.variant}`, `quiz-dla-${this.state.for ? 'niego' : 'siebie'}`];
    for (const pk of [].concat(a.produkt ?? [])) t.push(`quiz-${pk}`);
    if (a.wiek) t.push(`quiz-wiek-${a.wiek}`);
    if (a.kto) t.push(`quiz-kupuje-${a.kto}`);
    if (a.wydaje) t.push(`quiz-wydaje-${a.wydaje}`);
    if ((a.brakuje || '').length >= 40) t.push('kandydat-tester');
    for (const z of [].concat(a.zapach ?? [])) t.push(`quiz-zapach-${z}`);
    return t;
  }

  /** (unused) kept for reference: fetch-based lite submit is blocked by Shopify's bot challenge */
  async submitLite(name, email, payload) {
    const fd = new FormData();
    fd.set('form_type', 'customer'); fd.set('utf8', '✓');
    fd.set('contact[first_name]', name); fd.set('contact[email]', email);
    fd.set('contact[tags]', this.tags().join(', '));
    fd.set('contact[note]', JSON.stringify({ answers: payload.answers, product: payload.product, utm: payload.utm, variant: payload.variant }).slice(0, 5000));
    const r = await fetch('/contact', { method: 'POST', body: fd, redirect: 'follow' });
    return r.ok;
  }

  /* ---------- end screen ---------- */
  productByKey(k) { return this.products.find((p) => p.key === k) || null; }

  renderEnd() {
    const p = this.productByKey(this.state.product) || this.products[0];
    const name = this.state.meta.contact?.name || '';
    const scents = [].concat(this.state.answers.zapach ?? []);
    const zOpts = this.cfg.questions.find((q) => q.id === 'zapach')?.options || [];
    const scentOpt = scents.map((v) => zOpts.find((o) => o.v === v)).find((o) => o && o.scent) || null;
    const scentName = scentOpt ? { 'smolny-bor': 'Smolny Bór', 'miod-pitny': 'Miód Pitny', 'zimny-baltyk': 'Zimny Bałtyk', 'stara-tabakiera': 'Stara Tabakiera', 'debowa-beczka': 'Dębowa Beczka' }[scentOpt.scent] : null;
    const his = this.state.meta.contact?.his_name;
    this.$('[data-dp-end-h2]').textContent = this.state.for ? `${name ? name + ', p' : 'P'}rodukt na start dla niego:` : `${name ? name + ', t' : 'T'}wój produkt na start:`;
    this.$('[data-dp-code]').textContent = this.state.code || '—';
    this.$('[data-dp-coupon-sub]').textContent = this.dataset.coupon || '';
    const prod = this.$('[data-dp-end-product]');
    const scentImg = scentOpt && p?.scentUrls ? `${this.assetBase()}${p.key === 'mydlo' ? scentOpt.scent + '-01-packshot-transparent.png' : (p.key === 'deo' ? 'dezodorant' : p.key) + '-' + scentOpt.scent + '-01-packshot-transparent.png'}${this.dataset.assetV ? '?v=' + this.dataset.assetV : ''}` : p?.img;
    prod.innerHTML = p ? `${scentImg ? `<img src="${scentImg}" alt="" onerror="this.src='${p.img}'">` : ''}<div><div class="dp-quiz__product-name">${p.endName || p.name}${scentName && p.scentUrls ? ' · ' + scentName : ''}</div><div class="dp-quiz__product-meta">${p.size} · ${p.priceLabel}${p.feature ? ' · ' + p.feature : ''}</div></div>` : '';
    this.$('[data-dp-presale]').innerHTML = p ? (p.preorder ? `<strong>Przedsprzedaż.</strong> Wysyłka: ${p.ship}. Płacisz teraz, możesz anulować do wysyłki, oddajemy 100 %. Przy zestawie na prezent: nie zdążymy na święta. Na Walentynki — bez problemu.` : `<strong>W przygotowaniu.</strong> Planowana wysyłka: ${p.ship}. Zarezerwuj, a napiszemy, jak ruszy przedsprzedaż.`) : '';
    const pre = this.$('[data-dp-preorder]');
    let url = p?.url || '';
    if (scentOpt && p?.scentUrls) { const m = Object.fromEntries(p.scentUrls.split('\n').map((l) => l.split('|').map((x) => x.trim())).filter((x) => x.length === 2)); url = m[scentOpt.scent] || url; }
    if (url && this.state.code && !this.lite) url = `/discount/${encodeURIComponent(this.state.code)}?redirect=${encodeURIComponent(url)}`;
    const utm = new URLSearchParams(this.state.utm).toString();
    if (url && utm) url += (url.includes('?') ? '&' : '?') + utm;
    pre.href = url || '#'; pre.hidden = !url;
    pre.textContent = p?.preorder ? (pre.dataset.labelPreorder || pre.textContent) : (pre.dataset.labelView || pre.textContent);

    this.$('[data-dp-reserved]').hidden = this.state.end_action !== 'reserve';
    this.$('[data-dp-reserve]').hidden = this.state.end_action === 'reserve';
  }

  preorder(e) {
    this.state.end_action = 'preorder'; this.save();
    this.pixel('InitiateCheckout', { product: this.state.product, content_name: 'quiz-preorder' });
    this.track('end_action', { action: 'preorder', product: this.state.product });
    this.sendEndAction('preorder');
    this.flush(true);
  }

  reserve() {
    this.state.end_action = 'reserve'; this.save(); this.renderEnd();
    this.pixel('Reserve', { product: this.state.product }, true);
    this.track('end_action', { action: 'reserve', product: this.state.product });
    this.sendEndAction('reserve');
  }

  sendEndAction(action) {
    if (this.sheet) { this.beacon(this.endpoint, { type: 'end', session: this.state.session, email: this.state.meta.contact?.email || null, action, product: this.state.product, products: this.state.answers.produkt || [] }); return; }
    if (this.lite) return;
    fetch(`${this.endpoint}/quiz/end`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ session: this.state.session, email: null, action, product: this.state.product }), keepalive: true }).catch(() => {});
  }
}

if (!customElements.get('dp-quiz')) customElements.define('dp-quiz', DpQuiz);
